import { useMemo } from 'react';
import { LICENSE_STATE_ACTIVE } from '@jira/platform__license-state/src/license-state.tsx';
import {
	FULL_ISSUE,
	EMBED_ISSUE,
} from '@atlassian/jira-common-constants/src/analytics-sources.tsx';
import {
	SOFTWARE_PROJECT,
	CORE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import type { ChildIssueFieldKeys } from '@atlassian/jira-issue-shared-types/src/common/types/children-issues-type.tsx';
import {
	useProjectKey as useProjectKeyNew,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useIssueContextStoreChildIssuesIssueLimitUrls,
	useIssueContextStoreChildIssuesLimit,
	useIssueContextStoreIsModal,
	useIssueContextStoreIssueKey,
	useIssueContextStoreIssueId,
	useIssueContextStoreAnalyticsSource,
	useIssueContextRapidViewMap,
	useIssueContextStoreMaxTokenLength,
	useIssueContextStoreIsLoadedWithPage,
	useIssueContextStoreEstimationStatistic,
	useIssueContextStoreIsLoading,
	useIssueContextStoreViewMode,
	useIssueContextStoreBackButtonUrl,
	useIssueContextStoreIssueNavigatorData,
	useIssueContextStoreBoardId,
	useIssueContextStoreBoardType,
	useIssueContextStoreIsSpaEnabled,
	useIssueContextStoreLoginRedirctUrl,
	useIssueContextStoreOnIssueKeyChange,
	useIssueContextStoreRapidViewId,
	useIssueContextStoreViewScreenId,
	useIssueContextStoreActions,
	useIssueContextStoreIsArchived,
	useIssueContextStoreIsClassifyEditEnabled,
	useIssueContextIsAiEnabledForIssue,
	useAiFeaturesOnboarding,
} from './context.tsx';

export const useChildIssuesLimit = () => {
	const [childIssuesLimit] = useIssueContextStoreChildIssuesLimit();
	return childIssuesLimit;
};

export const useChildIssuesIssueLimitUrls = () => {
	const [childIssuesIssueLimitUrls] = useIssueContextStoreChildIssuesIssueLimitUrls();
	return childIssuesIssueLimitUrls;
};

export const useChildIssuesIssueLimitUrl = (fieldId: ChildIssueFieldKeys) => {
	const childIssuesIssueLimitUrls = useChildIssuesIssueLimitUrls();
	return childIssuesIssueLimitUrls?.[fieldId];
};

export const useIsModal = (): boolean => {
	const [isModal] = useIssueContextStoreIsModal();
	return isModal;
};

export const useIssueKey = (): string => {
	const [issueKey] = useIssueContextStoreIssueKey();
	return issueKey;
};

export const useIssueId = () => {
	const [issueId] = useIssueContextStoreIssueId();
	return issueId;
};

export const useIsArchived = () => {
	const [isArchived] = useIssueContextStoreIsArchived();
	return isArchived;
};

export const useIsClassifyEditEnabled = () => {
	const [isClassifyEditEnabled] = useIssueContextStoreIsClassifyEditEnabled();
	return isClassifyEditEnabled;
};

/**
 * @deprecated Please use the useProjectKey hook from @atlassian/jira-project-context-service/src/main.tsx (BENTO-8752)
 * @returns {ProjectKey}
 */
export const useProjectKey = (): string => {
	const [issueKey] = useIssueContextStoreIssueKey();
	return toProjectKey(issueKey.split('-')[0]);
};

export const useAnalyticsSource = (): string | undefined => {
	const [analyticsSource] = useIssueContextStoreAnalyticsSource();
	return analyticsSource;
};

export const useIssueContextActions = () => {
	const [
		,
		{
			setIssueContext,
			mergeIssueContext,
			getChildIssuesLimit,
			getMaxTokenLength,
			getIsLoadedWithPage,
			getBackButtonUrl,
			getIsSpaEnabled,
			getIssueKey,
			setDetailViewAsModal,
			setDetailViewAsSidebar,
			resetDetailView,
			setAiFeaturesOnboardingSpotlight,
		},
	] = useIssueContextStoreActions() || [null, {}];

	const actions = useMemo(
		() => ({
			setIssueContext,
			mergeIssueContext,
			getChildIssuesLimit,
			getMaxTokenLength,
			getIsLoadedWithPage,
			getBackButtonUrl,
			getIsSpaEnabled,
			getIssueKey,
			setDetailViewAsModal,
			setDetailViewAsSidebar,
			resetDetailView,
			setAiFeaturesOnboardingSpotlight,
		}),
		[
			setIssueContext,
			mergeIssueContext,
			getChildIssuesLimit,
			getMaxTokenLength,
			getIsLoadedWithPage,
			getBackButtonUrl,
			getIsSpaEnabled,
			getIssueKey,
			setDetailViewAsModal,
			setDetailViewAsSidebar,
			resetDetailView,
			setAiFeaturesOnboardingSpotlight,
		],
	);
	return [undefined, actions] as const;
};

export const useRapidViewMap = () => {
	const [rapidViewMap] = useIssueContextRapidViewMap();
	return rapidViewMap;
};

export const useMaxTokenLength = (): number => {
	const [maxTokenLength] = useIssueContextStoreMaxTokenLength();
	return maxTokenLength;
};

export const useIsLoadedWithPage = (): boolean => {
	const [isLoadedWithPage] = useIssueContextStoreIsLoadedWithPage();
	return isLoadedWithPage;
};

export const useEstimationStatistic = () => {
	const [estimationStatistic] = useIssueContextStoreEstimationStatistic();
	return estimationStatistic;
};

export const useIsLoading = (): boolean => {
	const [isLoading] = useIssueContextStoreIsLoading();
	return isLoading;
};

export const useViewMode = () => {
	const [viewMode] = useIssueContextStoreViewMode();

	return viewMode;
};

export const useBackButtonUrl = () => {
	const [backButtonUrl] = useIssueContextStoreBackButtonUrl();
	return backButtonUrl;
};

export const useIssueNavigatorData = () => {
	const [issueNavigatorData] = useIssueContextStoreIssueNavigatorData();
	return issueNavigatorData;
};

export const useBoardId = () => {
	const [boardId] = useIssueContextStoreBoardId();
	return boardId;
};

export const useBoardType = () => {
	const [boardType] = useIssueContextStoreBoardType();
	return boardType;
};

export const useIsSpaEnabled = (): boolean => {
	const [isSpaEnabled] = useIssueContextStoreIsSpaEnabled();
	return isSpaEnabled;
};

export const useLoginRedirectUrl = () => {
	const [loginRedirectUrl] = useIssueContextStoreLoginRedirctUrl();
	return loginRedirectUrl;
};

export const useOnIssueKeyChange = () => {
	const [onIssueKeyChange] = useIssueContextStoreOnIssueKeyChange();
	return onIssueKeyChange;
};

export const useRapidViewId = () => {
	const [rapidViewId] = useIssueContextStoreRapidViewId();
	return rapidViewId;
};

export const useViewScreenId = () => {
	const [viewScreenId] = useIssueContextStoreViewScreenId();
	return viewScreenId;
};

export const useHasSoftwareAccess = (): boolean => {
	const licensedProducts = useLicensedProducts();
	const licenseStates = useLicenseStates();
	const isJiraSoftwareLicenseActive =
		licenseStates && licenseStates.software === LICENSE_STATE_ACTIVE;

	return licensedProducts[LICENSED_PRODUCTS.JIRA_SOFTWARE] && isJiraSoftwareLicenseActive;
};

export const useIsFullPageIssueView = (): boolean => {
	const analyticsSource = useAnalyticsSource();
	return analyticsSource === FULL_ISSUE;
};

export const useIsEmbedPageIssueView = (): boolean => {
	const analyticsSource = useAnalyticsSource();
	return analyticsSource === EMBED_ISSUE;
};

export const useGetAiFeaturesOnboarding = () => {
	const [aiFeaturesOnboardingSpotlight] = useAiFeaturesOnboarding();
	return aiFeaturesOnboardingSpotlight;
};

/**
 * isAiEnabledForIssue is merged into the issue context store
 * at issue-fetch time, allowing faster retrieval.
 */
export const useIsAiEnabledForIssue = (): boolean => {
	const [isAiEnabledForIssue] = useIssueContextIsAiEnabledForIssue();
	return isAiEnabledForIssue;
};

export const useIsJiraIssue = (): boolean => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKeyNew(issueKey);
	const projectType = useProjectType(projectKey);
	return [SOFTWARE_PROJECT, CORE_PROJECT].some((type) => type === projectType);
};

export const useShouldUseRelayIssueComments = (): boolean => {
	const isJiraIssue = useIsJiraIssue();

	return isJiraIssue && fg('relay-migration-issue-comments');
};

export const useShouldUseRelayIssueHistory = (): boolean => {
	const isJiraIssue = useIsJiraIssue();

	return isJiraIssue && fg('relay-migration-issue-history');
};
